import { useFillerPreviewSources } from '@/hooks/use-filler-preview-sources';
// src/components/layouts/LayoutSettingsPanel.tsx
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { GET_FILLER_VIDEO_TAGS, GET_MY_USER_MEDIA } from '@/queries/queries';
import { useRegisterUserMedia } from '@/hooks/use-register-user-media';
import type { FieldDescriptor, PostLayoutConfig } from '@/lib/layouts/types';
import { useUploadFile } from '@/hooks/use-upload-file';
import { useVideoFrameExtractor } from '@/hooks/use-video-frame-extractor';
import { useFillerVideosQuery } from '@/generated/graphql';
import ColorInput from '@/components/ColorInput';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import FontSelector from '@/components/FontSelector';
import { MyMediaPickerDialog } from '@/components/media/MyMediaPickerDialog';
import { GripVertical, Shuffle, ArrowUpToLine, ArrowDownToLine, AlignCenterVertical, Camera, ChevronLeft, ChevronRight, Images, Loader2, Upload } from 'lucide-react';
import { FillerVideoBrowseDialog, type FillerVideoTag } from './FillerVideoBrowseDialog';
import {
  resolveFillerPlaylist,
  reorderPlaylist,
  pickRandomFillersToFill,
  isImageFillerUrl,
  AVG_FILLER_DURATION_SEC,
  type FillerFit,
  type FillerPosition,
} from '@/lib/layouts/fillerPlaylist';
import { applyModeToAll, uniformValue } from '@/lib/layouts/fillerBulkControls';
import type { PostLayoutFieldValue } from '@/lib/layouts/types';
import {
  bindControlForField,
  identityBindSourcesForField,
  type LayoutBindSource,
  type LayoutFieldBinds,
} from '@/lib/layouts/layoutFieldBinds';
import { resolveFofocaStoryMedia } from '@/lib/layouts/resolveFofocaStoryMedia';
import { formatVideoFrameSeconds } from '@/lib/preview/clampVideoFrameTime';
import { resolveCaptureTime, frameDataUrlToFile, type VideoFrameSource } from '@/lib/media/frameCapture';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface LayoutBindUiContext {
  clipTitle: string;
  channelName?: string;
  channelAvatarUrl?: string;
  userName?: string;
  userAvatarUrl?: string;
  hasChannel: boolean;
}

function bindToggleCopy(source: LayoutBindSource): { labelKey: string; hintKey: string } {
  if (source === 'clipTitle') {
    return {
      labelKey: 'projectClips.postLayouts.binds.clipTitle',
      hintKey: 'projectClips.postLayouts.binds.clipTitleHint',
    };
  }
  return {
    labelKey: 'projectClips.postLayouts.binds.channel',
    hintKey: source === 'channelName'
      ? 'projectClips.postLayouts.binds.channelNameHint'
      : 'projectClips.postLayouts.binds.channelAvatarHint',
  };
}

function resolveBindPreview(source: LayoutBindSource, ctx: LayoutBindUiContext): string {
  if (source === 'clipTitle') return ctx.clipTitle;
  if (source === 'channelName') return ctx.channelName ?? '';
  if (source === 'channelAvatar') return ctx.channelAvatarUrl ?? '';
  if (source === 'userName') return ctx.userName ?? '';
  return ctx.userAvatarUrl ?? '';
}

function identityBindCopy(source: LayoutBindSource): { labelKey: string; hintKey?: string; testId: string } {
  if (source === 'channelAvatar') {
    return {
      labelKey: 'projectClips.postLayouts.binds.useChannelAvatar',
      hintKey: 'projectClips.postLayouts.binds.useChannelWhenAvailable',
      testId: 'layout-use-channel-avatar',
    };
  }
  if (source === 'userAvatar') {
    return { labelKey: 'projectClips.postLayouts.binds.useMyAvatar', testId: 'layout-use-my-avatar' };
  }
  if (source === 'channelName') {
    return {
      labelKey: 'projectClips.postLayouts.binds.useChannelName',
      hintKey: 'projectClips.postLayouts.binds.useChannelWhenAvailable',
      testId: 'layout-use-channel-name',
    };
  }
  return { labelKey: 'projectClips.postLayouts.binds.useMyName', testId: 'layout-use-my-name' };
}

function IdentityBindButtons({
  fieldKey,
  sources,
  boundSource,
  disabled,
  onToggle,
}: {
  fieldKey: string;
  sources: LayoutBindSource[];
  boundSource?: LayoutBindSource;
  disabled: boolean;
  onToggle: (source: LayoutBindSource | null) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="absolute right-1 top-1/2 z-10 flex max-w-[75%] -translate-y-1/2 flex-wrap justify-end gap-0.5">
      {sources.map((source) => {
        const copy = identityBindCopy(source);
        const isBound = boundSource === source;
        const button = (
          <button
            type="button"
            data-testid={`${copy.testId}-${fieldKey}`}
            aria-pressed={isBound}
            disabled={disabled}
            onClick={() => onToggle(isBound ? null : source)}
            className={cn(
              'h-6 max-w-full truncate rounded-md border px-1.5 text-[10px] leading-none',
              isBound
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border bg-background/90 text-muted-foreground hover:bg-muted/50',
            )}
          >
            {t(copy.labelKey)}
          </button>
        );
        if (!copy.hintKey) return <span key={source}>{button}</span>;
        return (
          <Tooltip key={source}>
            <TooltipTrigger asChild>{button}</TooltipTrigger>
            <TooltipContent
              side="top"
              data-testid={`${copy.testId}-${fieldKey}-tooltip`}
              className="max-w-[220px] text-center"
            >
              {t(copy.hintKey)}
            </TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}

const CLIP_TITLE_INPUT_PAD = 'pr-[9.75rem]';
const IDENTITY_INPUT_PAD = 'pr-[55%]';

function ClipTitleBindToggle({
  fieldKey,
  isBound,
  disabled,
  onToggle,
  label,
  hint,
  align = 'center',
}: {
  fieldKey: string;
  isBound: boolean;
  disabled: boolean;
  onToggle: () => void;
  label: string;
  hint: string;
  align?: 'center' | 'start';
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          data-testid={`layout-use-clip-title-${fieldKey}`}
          aria-pressed={isBound}
          title={hint}
          disabled={disabled}
          onClick={onToggle}
          className={cn(
            'absolute right-1 z-10 h-6 max-w-[9.25rem] truncate rounded-md border px-1.5 text-[10px] leading-none',
            align === 'start' ? 'top-1' : 'top-1/2 -translate-y-1/2',
            isBound
              ? 'border-primary bg-primary/10 text-primary'
              : 'border-border bg-background/90 text-muted-foreground hover:bg-muted/50',
          )}
        >
          {label}
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        data-testid={`layout-use-clip-title-${fieldKey}-tooltip`}
        className="max-w-[220px] text-center"
      >
        {hint}
      </TooltipContent>
    </Tooltip>
  );
}

// Assumed average filler length used to size the auto-fill selection. The
// Default target when the caller doesn't know the clip duration.
const DEFAULT_FILL_TARGET_SEC = 90;

function captureFrameLabel(
  source: VideoFrameSource,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (source.captureLabel === 'atTime') {
    return t('projectClips.postLayouts.satisfatorio.useFrameAtSeconds', {
      seconds: formatVideoFrameSeconds(resolveCaptureTime(source)),
    });
  }
  return t('projectClips.postLayouts.fofoca.useCurrentFrame');
}

const ImageField: React.FC<{
  fieldKey: string;
  value: string;
  inputCls: string;
  disabled?: boolean;
  allowVideoFrame?: boolean;
  videoFrameSource?: VideoFrameSource;
  onChange: (key: string, value: string) => void;
  endAdornment?: React.ReactNode;
}> = ({ fieldKey, value, inputCls, disabled, allowVideoFrame, videoFrameSource, onChange, endAdornment }) => {
  const { t } = useTranslation();
  const { getUploadUrl, isLoading, error } = useUploadFile();
  const registerUserMedia = useRegisterUserMedia();
  const { extractFrame } = useVideoFrameExtractor(videoFrameSource?.videoUrl, {
    canvasWidth: 720,
    canvasHeight: 1210,
    quality: 0.9,
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [captureError, setCaptureError] = useState<string | null>(null);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = await getUploadUrl(file);
    if (url) {
      onChange(fieldKey, url);
      // Every upload lands in the user's media library for reuse.
      registerUserMedia({ url, kind: 'image', name: file.name });
    }
    // reset so selecting the same file again re-fires change
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCaptureFrame = async () => {
    if (!videoFrameSource || disabled || capturing) return;
    setCaptureError(null);
    setCapturing(true);
    const captureTime = resolveCaptureTime(videoFrameSource);
    try {
      const dataUrl = await extractFrame(captureTime);
      if (!dataUrl) throw new Error('Frame extraction failed');
      const file = frameDataUrlToFile(dataUrl, captureTime);
      const url = await getUploadUrl(file);
      if (!url) throw new Error('Frame upload failed');
      onChange(fieldKey, url);
      registerUserMedia({
        url,
        kind: 'image',
        name: file.name,
        width: 720,
        height: 1210,
      });
    } catch {
      setCaptureError(t('projectClips.postLayouts.fofoca.captureFrameError'));
    } finally {
      setCapturing(false);
    }
  };

  return (
    <div className="space-y-1.5">
      <div className="flex gap-2 items-center">
        {value && (
          <img src={value} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0 border border-border" />
        )}
        <div className="relative min-w-0 flex-1">
          <Input
            type="url"
            value={value}
            disabled={disabled}
            placeholder="https://..."
            onChange={(e) => onChange(fieldKey, e.target.value)}
            className={cn('w-full', inputCls, endAdornment && IDENTITY_INPUT_PAD)}
          />
          {endAdornment}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg"
          className="hidden"
          data-testid={`image-upload-${fieldKey}`}
          disabled={disabled}
          onChange={handleFile}
        />
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <button
          type="button"
          data-testid={`image-library-${fieldKey}`}
          disabled={disabled}
          onClick={() => setMediaPickerOpen(true)}
          className="flex items-center justify-center gap-1.5 text-xs px-2 py-1.5 rounded-md border border-border bg-muted/30 hover:bg-muted/50 disabled:opacity-50 whitespace-nowrap"
        >
          <Images className="h-3.5 w-3.5" aria-hidden="true" />
          {t('myMedia.picker.open')}
        </button>
        <button
          type="button"
          disabled={isLoading || disabled}
          onClick={() => fileInputRef.current?.click()}
          className="flex items-center justify-center gap-1.5 text-xs px-2 py-1.5 rounded-md border border-border bg-muted/30 hover:bg-muted/50 disabled:opacity-50 whitespace-nowrap"
        >
          {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <Upload className="h-3.5 w-3.5" aria-hidden="true" />}
          {isLoading ? t('projectClips.postLayouts.uploading') : t('projectClips.postLayouts.upload')}
        </button>
      </div>
      {allowVideoFrame && videoFrameSource && (
        <button
          type="button"
          data-testid={`capture-frame-${fieldKey}`}
          disabled={capturing || isLoading || disabled}
          onClick={handleCaptureFrame}
          className="w-full flex items-center justify-center gap-1.5 text-xs px-2 py-1.5 rounded-md border border-border bg-muted/30 hover:bg-muted/50 disabled:opacity-50"
        >
          {capturing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
          {capturing
            ? t('projectClips.postLayouts.fofoca.capturingFrame')
            : captureFrameLabel(videoFrameSource, t)}
        </button>
      )}
      {(error || captureError) && <p className="text-[11px] text-destructive">{captureError || error}</p>}
      <MyMediaPickerDialog
        open={mediaPickerOpen}
        onOpenChange={setMediaPickerOpen}
        onSelect={(item) => onChange(fieldKey, item.url)}
        onUploadInstead={() => {
          setMediaPickerOpen(false);
          fileInputRef.current?.click();
        }}
      />
    </div>
  );
};

const VideoField: React.FC<{
  fieldKey: string;
  value: string;
  /** Effective filler playlist (fillerUrls, falling back to the single value). */
  playlist: string[];
  /** Clip duration used to size the auto-fill random selection. */
  targetDurationSec?: number;
  /** Playback speed, so auto-fill counts speed-adjusted lengths. */
  fillerSpeed?: number;
  /** Per-video fit map (url -> 'fit' | 'fill'). */
  fits?: Record<string, FillerFit>;
  /** Per-video vertical anchor map (url -> 'top' | 'center' | 'bottom'). */
  positions?: Record<string, FillerPosition>;
  urlsKey?: string;
  fitsKey?: string;
  positionsKey?: string;
  randomizeKey?: string;
  disabled?: boolean;
  videoFrameSource?: VideoFrameSource;
  deferRandomFillers?: boolean;
  fillerRandomize?: boolean;
  allowStillImages?: boolean;
  onChange: (key: string, value: PostLayoutFieldValue) => void;
}> = ({
  fieldKey,
  value,
  playlist,
  targetDurationSec,
  fillerSpeed,
  fits,
  positions,
  urlsKey = 'fillerUrls',
  fitsKey = 'fillerFits',
  positionsKey = 'fillerPositions',
  randomizeKey = 'fillerRandomize',
  disabled,
  videoFrameSource,
  deferRandomFillers,
  fillerRandomize,
  allowStillImages = true,
  onChange,
}) => {
  const { t, i18n } = useTranslation();
  const { getUploadUrl, isLoading, error } = useUploadFile();
  const { data, loading } = useFillerVideosQuery();
  const fillerPreviews = useFillerPreviewSources();
  const registerUserMedia = useRegisterUserMedia();
  const { extractFrame } = useVideoFrameExtractor(videoFrameSource?.videoUrl, {
    canvasWidth: 720,
    canvasHeight: 1210,
    quality: 0.9,
  });
  const { data: myMediaData } = useQuery(GET_MY_USER_MEDIA, { variables: { kind: 'video' } });
  const { data: tagsData } = useQuery(GET_FILLER_VIDEO_TAGS);
  const myVideos: { id: string; url: string; name?: string | null }[] =
    myMediaData?.myUserMedia ?? [];
  const tags: FillerVideoTag[] = tagsData?.fillerVideoTags ?? [];
  const fileInputRef = useRef<HTMLInputElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const selectedCarouselRef = useRef<HTMLButtonElement>(null);
  const isDragging = useRef(false);
  const hasDragged = useRef(false);
  const dragStartX = useRef(0);
  const scrollStartX = useRef(0);
  const [browseOpen, setBrowseOpen] = useState(false);
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [captureError, setCaptureError] = useState<string | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const dragIndex = useRef<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const presets = data?.fillerVideos ?? [];
  const isEnglish = (i18n?.language ?? '').toLowerCase().startsWith('en');
  // English UI gets the en-language variant when the preset has one.
  const presetUrl = (p: { file_url: string; file_en_url?: string | null }) =>
    (isEnglish && p.file_en_url) || p.file_url;

  const selectedPreset = presets.find((p) => value && value === presetUrl(p));
  const selectedMyVideo = myVideos.find((m) => value && value === m.url);
  const carouselPresets = selectedPreset
    ? [selectedPreset, ...presets.filter((p) => p !== selectedPreset)]
    : presets;

  const updateScrollEdges = useCallback(() => {
    const scroller = carouselRef.current;
    if (!scroller) return;
    setCanScrollLeft(scroller.scrollLeft > 1);
    setCanScrollRight(scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1);
  }, []);

  useLayoutEffect(() => {
    const scroller = carouselRef.current;
    if (!scroller) return;
    updateScrollEdges();
    scroller.addEventListener('scroll', updateScrollEdges, { passive: true });
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateScrollEdges);
    observer?.observe(scroller);
    return () => {
      scroller.removeEventListener('scroll', updateScrollEdges);
      observer?.disconnect();
    };
  }, [updateScrollEdges, carouselPresets.length]);

  useEffect(() => {
    selectedCarouselRef.current?.scrollIntoView?.({
      inline: 'center',
      block: 'nearest',
      behavior: 'smooth',
    });
  }, [value]);
  const selectedName =
    selectedPreset?.name ??
    selectedMyVideo?.name ??
    (value ? t('projectClips.postLayouts.satisfatorio.customVideo') : null);

  /** Display name for an arbitrary playlist url. */
  const nameFor = (url: string) =>
    presets.find((p) => presetUrl(p) === url)?.name ??
    myVideos.find((m) => m.url === url)?.name ??
    t('projectClips.postLayouts.satisfatorio.customVideo');

  // The playlist and the legacy single field are written together; fillerUrl
  // mirrors the playlist head so pre-playlist renderers and enforced-layout
  // merges keep working (see resolveFillerPlaylist).
  const applyPlaylist = (urls: string[]) => {
    onChange(randomizeKey, false);
    onChange(urlsKey, urls);
    onChange(fieldKey, urls[0] ?? '');
  };

  const removeFromPlaylist = (url: string) => {
    applyPlaylist(playlist.filter((u) => u !== url));
    if (fits?.[url]) {
      const next = { ...fits };
      delete next[url];
      onChange(fitsKey, next);
    }
    if (positions?.[url]) {
      const next = { ...positions };
      delete next[url];
      onChange(positionsKey, next);
    }
  };

  const fitOf = (url: string): FillerFit => (fits?.[url] === 'fit' ? 'fit' : 'fill');
  const setFit = (url: string, mode: FillerFit) =>
    onChange(fitsKey, { ...(fits ?? {}), [url]: mode });

  const posOf = (url: string): FillerPosition => positions?.[url] ?? 'center';
  const setPos = (url: string, mode: FillerPosition) =>
    onChange(positionsKey, { ...(positions ?? {}), [url]: mode });

  // Bulk "apply to all": one write per map, replacing every per-item override.
  const uniformFit = uniformValue(playlist, fits, 'fill' as FillerFit);
  const uniformPos = uniformValue(playlist, positions, 'center' as FillerPosition);
  const anyFill = playlist.some((url) => fitOf(url) === 'fill');
  const setFitForAll = (mode: FillerFit) => onChange(fitsKey, applyModeToAll(playlist, mode));
  const setPosForAll = (mode: FillerPosition) => onChange(positionsKey, applyModeToAll(playlist, mode));

  const FitToggle: React.FC<{
    value: FillerFit | null;
    onSelect: (mode: FillerFit) => void;
    toggleDisabled?: boolean;
    testId: string;
  }> = ({ value, onSelect, toggleDisabled, testId }) => (
    <div className="flex rounded-md border border-border overflow-hidden flex-shrink-0">
      {(['fill', 'fit'] as const).map((mode) => (
        <button
          key={mode}
          type="button"
          data-testid={`${testId}-${mode}`}
          disabled={toggleDisabled}
          aria-pressed={value === mode}
          onClick={() => !toggleDisabled && onSelect(mode)}
          className={`text-[9px] px-1.5 py-0.5 disabled:opacity-50 ${
            value === mode ? 'bg-primary/15 text-primary font-semibold' : 'text-muted-foreground hover:bg-muted/50'
          }`}
        >
          {t(mode === 'fill' ? 'projectClips.postLayouts.satisfatorio.fillVideo' : 'projectClips.postLayouts.satisfatorio.fitVideo')}
        </button>
      ))}
    </div>
  );

  // Vertical anchor for the crop — only meaningful for 'fill'. Icons: top /
  // center / bottom.
  const PositionToggle: React.FC<{
    value: FillerPosition | null;
    onSelect: (mode: FillerPosition) => void;
    toggleDisabled?: boolean;
    testId: string;
  }> = ({ value, onSelect, toggleDisabled, testId }) => {
    const opts: { mode: FillerPosition; Icon: typeof ArrowUpToLine; labelKey: string }[] = [
      { mode: 'top', Icon: ArrowUpToLine, labelKey: 'projectClips.postLayouts.satisfatorio.positionTopFirst' },
      { mode: 'center', Icon: AlignCenterVertical, labelKey: 'projectClips.postLayouts.satisfatorio.positionCenter' },
      { mode: 'bottom', Icon: ArrowDownToLine, labelKey: 'projectClips.postLayouts.satisfatorio.positionBottomFirst' },
    ];
    return (
      <div className="flex rounded-md border border-border overflow-hidden flex-shrink-0">
        {opts.map(({ mode, Icon, labelKey }) => (
          <button
            key={mode}
            type="button"
            data-testid={`${testId}-${mode}`}
            disabled={toggleDisabled}
            aria-pressed={value === mode}
            title={t(labelKey)}
            aria-label={t(labelKey)}
            onClick={() => !toggleDisabled && onSelect(mode)}
            className={`px-1 py-0.5 disabled:opacity-40 ${
              value === mode ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:bg-muted/50'
            }`}
          >
            <Icon className="h-3 w-3" />
          </button>
        ))}
      </div>
    );
  };

  const handleDrop = (to: number) => {
    const from = dragIndex.current;
    dragIndex.current = null;
    setDragOverIndex(null);
    if (from === null || from === to) return;
    applyPlaylist(reorderPlaylist(playlist, from, to));
  };

  // Auto-fill: replace the sequence with a random spread of distinct library
  // videos covering the clip's duration. Preset urls only (the shared,
  // reusable catalog) — the user's private uploads aren't randomized in.
  const autoFillRandom = () => {
    if (deferRandomFillers) {
      onChange(randomizeKey, true);
      onChange(urlsKey, []);
      onChange(fieldKey, '');
      return;
    }
    const poolUrls = presets.map((p) => presetUrl(p));
    const urls = pickRandomFillersToFill(poolUrls, {
      targetDurationSec: targetDurationSec ?? DEFAULT_FILL_TARGET_SEC,
      avgDurationSec: AVG_FILLER_DURATION_SEC,
      fillerSpeed: fillerSpeed ?? 1,
    });
    if (urls.length > 0) applyPlaylist(urls);
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = await getUploadUrl(file);
    if (url) {
      const isImage = file.type.startsWith('image/');
      // Append to the sequence (deduped) so uploads build a playlist rather
      // than replacing it; images play as static frames in the rotation.
      applyPlaylist(playlist.includes(url) ? playlist : [...playlist, url]);
      // Every upload lands in the user's media library for reuse.
      registerUserMedia({ url, kind: isImage ? 'image' : 'video', name: file.name });
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCaptureFrame = async () => {
    if (!videoFrameSource?.videoUrl || disabled || capturing) return;
    setCaptureError(null);
    setCapturing(true);
    const captureTime = resolveCaptureTime(videoFrameSource);
    try {
      const dataUrl = await extractFrame(captureTime);
      if (!dataUrl) throw new Error('Frame extraction failed');
      const file = frameDataUrlToFile(dataUrl, captureTime);
      const url = await getUploadUrl(file);
      if (!url) throw new Error('Frame upload failed');
      applyPlaylist([url]);
      registerUserMedia({
        url,
        kind: 'image',
        name: file.name,
        width: 720,
        height: 1210,
      });
    } catch {
      setCaptureError(t('projectClips.postLayouts.fofoca.captureFrameError'));
    } finally {
      setCapturing(false);
    }
  };

  const endCarouselDrag = () => {
    isDragging.current = false;
    const scroller = carouselRef.current;
    if (!scroller) return;
    scroller.style.cursor = 'grab';
    scroller.style.removeProperty('user-select');
    scroller.style.removeProperty('scroll-snap-type');
  };

  const handleCarouselMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    if (event.button !== 0 || !carouselRef.current) return;
    isDragging.current = true;
    hasDragged.current = false;
    dragStartX.current = event.clientX;
    scrollStartX.current = carouselRef.current.scrollLeft;
    carouselRef.current.style.cursor = 'grabbing';
    carouselRef.current.style.userSelect = 'none';
    carouselRef.current.style.scrollSnapType = 'none';
  };

  const handleCarouselMouseMove = (event: MouseEvent<HTMLDivElement>) => {
    if (!isDragging.current || !carouselRef.current) return;
    const dx = event.clientX - dragStartX.current;
    if (Math.abs(dx) > 3) hasDragged.current = true;
    carouselRef.current.scrollLeft = scrollStartX.current - dx;
  };

  const scrollCarousel = (direction: -1 | 1) => {
    const scroller = carouselRef.current;
    if (!scroller) return;
    const amount = Math.max(scroller.clientWidth * 0.7, 124);
    scroller.scrollBy({ left: direction * amount, behavior: 'smooth' });
  };

  return (
    <div className="space-y-1.5">
      {loading && <p className="text-[11px] text-muted-foreground">…</p>}
      {!loading && presets.length === 0 && (
        <p className="text-[11px] text-muted-foreground">
          {t('projectClips.postLayouts.satisfatorio.noPresets')}
        </p>
      )}
      {playlist.length > 1 ? (
        <div data-testid="filler-playlist" className="space-y-1">
          <p className="text-[11px] font-semibold text-muted-foreground">
            {t('projectClips.postLayouts.satisfatorio.playlistLabel', { count: playlist.length })}
          </p>
          {/* Bulk controls: style every video in the sequence with one click. */}
          <div
            data-testid="filler-bulk-controls"
            className="flex items-center gap-1.5 rounded-md border border-border bg-muted/20 px-1.5 py-1"
          >
            <span className="text-[10px] text-muted-foreground flex-1 truncate">
              {t('projectClips.postLayouts.satisfatorio.allVideosLabel')}
            </span>
            <FitToggle value={uniformFit} onSelect={setFitForAll} toggleDisabled={disabled} testId="filler-bulk-fit" />
            <PositionToggle value={uniformPos} onSelect={setPosForAll} toggleDisabled={disabled || !anyFill} testId="filler-bulk-pos" />
          </div>
          {playlist.map((url, i) => (
            <div
              key={url}
              data-testid={`filler-playlist-item-${i}`}
              draggable={!disabled}
              onDragStart={() => { dragIndex.current = i; }}
              onDragOver={(e) => { e.preventDefault(); if (dragOverIndex !== i) setDragOverIndex(i); }}
              onDrop={(e) => { e.preventDefault(); handleDrop(i); }}
              onDragEnd={() => { dragIndex.current = null; setDragOverIndex(null); }}
              className={`rounded-md border bg-muted/30 p-1.5 space-y-1.5 ${
                dragOverIndex === i ? 'border-primary' : 'border-border'
              } ${disabled ? '' : 'cursor-grab active:cursor-grabbing'}`}
            >
              <div className="flex items-center gap-2">
                <GripVertical
                  className={`h-3.5 w-3.5 flex-shrink-0 text-muted-foreground/60 ${disabled ? 'opacity-40' : ''}`}
                  aria-hidden="true"
                />
                <span className="text-[10px] font-bold text-muted-foreground w-4 text-center flex-shrink-0">
                  {i + 1}
                </span>
                <div className="h-8 w-6 rounded overflow-hidden bg-black/40 flex-shrink-0">
                  {(() => {
                    const thumb = presets.find((p) => presetUrl(p) === url)?.thumbnail_url;
                    if (thumb) return <img src={thumb} alt="" className="w-full h-full object-cover" />;
                    return isImageFillerUrl(url)
                      ? <img src={url} alt="" className="w-full h-full object-cover" />
                      : <video src={fillerPreviews[url]?.url ?? url} muted preload="metadata" className="w-full h-full object-cover pointer-events-none" />;
                  })()}
                </div>
                <p className="text-[11px] flex-1 truncate">{nameFor(url)}</p>
                <button
                  type="button"
                  data-testid={`filler-playlist-remove-${i}`}
                  disabled={disabled}
                  onClick={() => !disabled && removeFromPlaylist(url)}
                  className="text-xs px-1.5 text-muted-foreground hover:text-destructive disabled:opacity-50"
                  aria-label={t('projectClips.postLayouts.satisfatorio.removeFromPlaylist')}
                >
                  ×
                </button>
              </div>
              {/* Per-video controls: fit/fill + which slice to show. */}
              <div className="flex items-center gap-1.5 pl-6">
                <FitToggle value={fitOf(url)} onSelect={(m) => setFit(url, m)} toggleDisabled={disabled} testId={`filler-playlist-fit-${i}`} />
                <PositionToggle value={posOf(url)} onSelect={(m) => setPos(url, m)} toggleDisabled={disabled || fitOf(url) !== 'fill'} testId={`filler-playlist-pos-${i}`} />
              </div>
            </div>
          ))}
        </div>
      ) : selectedName && (
        <div
          data-testid="filler-selected-card"
          className="rounded-md border border-border bg-muted/30 p-1.5 space-y-1.5"
        >
          <div className="flex items-center gap-2">
            <div className="h-10 w-7 rounded overflow-hidden bg-black/40 flex-shrink-0">
              {selectedPreset?.thumbnail_url ? (
                <img src={selectedPreset.thumbnail_url} alt="" className="w-full h-full object-cover" />
              ) : isImageFillerUrl(value) ? (
                <img src={value} alt="" className="w-full h-full object-cover" />
              ) : (
                <video src={fillerPreviews[value]?.url ?? value} muted preload="metadata" className="w-full h-full object-cover pointer-events-none" />
              )}
            </div>
            <p className="text-[11px] flex-1 truncate">{selectedName}</p>
            {value && (
              <button
                type="button"
                data-testid="filler-selected-remove"
                disabled={disabled}
                onClick={() => !disabled && removeFromPlaylist(value)}
                className="text-xs px-1.5 text-muted-foreground hover:text-destructive disabled:opacity-50"
                aria-label={t('projectClips.postLayouts.satisfatorio.removeSelectedVideo')}
                title={t('projectClips.postLayouts.satisfatorio.removeSelectedVideo')}
              >
                ×
              </button>
            )}
          </div>
          {value && (
            <div className="flex items-center gap-1.5">
              <FitToggle value={fitOf(value)} onSelect={(m) => setFit(value, m)} toggleDisabled={disabled} testId="filler-selected-fit" />
              <PositionToggle value={posOf(value)} onSelect={(m) => setPos(value, m)} toggleDisabled={disabled || fitOf(value) !== 'fill'} testId="filler-selected-pos" />
            </div>
          )}
        </div>
      )}
      {carouselPresets.length > 0 && (
        <div className="relative">
          <div
            className="pointer-events-none absolute inset-y-0 left-0 z-10 w-7 bg-gradient-to-r from-card to-transparent"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-y-0 right-0 z-10 w-7 bg-gradient-to-l from-card to-transparent"
            aria-hidden
          />
          <button
            type="button"
            data-testid="filler-video-carousel-prev"
            aria-label={t('projectClips.postLayouts.satisfatorio.carouselPrevious')}
            disabled={!canScrollLeft}
            onClick={() => scrollCarousel(-1)}
            className={cn(
              'absolute left-1 top-1/2 z-20 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-background/95 shadow-sm',
              canScrollLeft ? 'opacity-100' : 'pointer-events-none opacity-0',
            )}
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            data-testid="filler-video-carousel-next"
            aria-label={t('projectClips.postLayouts.satisfatorio.carouselNext')}
            disabled={!canScrollRight}
            onClick={() => scrollCarousel(1)}
            className={cn(
              'absolute right-1 top-1/2 z-20 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-background/95 shadow-sm',
              canScrollRight ? 'opacity-100' : 'pointer-events-none opacity-0',
            )}
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
          <div
            ref={carouselRef}
            data-testid="filler-video-carousel"
            className="flex cursor-grab gap-2 overflow-x-auto snap-x snap-mandatory pb-1 scrollbar-hide"
            style={{ scrollbarWidth: 'none' }}
            onMouseDown={handleCarouselMouseDown}
            onMouseMove={handleCarouselMouseMove}
            onMouseUp={endCarouselDrag}
            onMouseLeave={endCarouselDrag}
          >
            {carouselPresets.map((p) => {
              const url = presetUrl(p);
              const isSelected = value === url;
              return (
                <button
                  key={p.id}
                  ref={isSelected ? selectedCarouselRef : undefined}
                  type="button"
                  data-testid={`filler-preset-${p.id}`}
                  title={p.name}
                  disabled={disabled}
                  onClick={() => {
                    if (disabled || hasDragged.current) return;
                    applyPlaylist([url]);
                  }}
                  className={cn(
                    'relative aspect-video w-[7.25rem] shrink-0 snap-start overflow-hidden rounded-lg border-2 text-left disabled:opacity-50 disabled:cursor-not-allowed',
                    isSelected
                      ? 'border-primary shadow-[0_0_8px_-2px] shadow-primary/40'
                      : 'border-border hover:border-white/30',
                  )}
                >
                  {p.thumbnail_url
                    ? <img src={p.thumbnail_url} alt={p.name} loading="lazy" className="pointer-events-none h-full w-full object-cover" />
                    : <span className="line-clamp-2 px-1 text-[10px]">{p.name}</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-1.5">
        <input
          ref={fileInputRef}
          type="file"
          accept={allowStillImages ? 'video/mp4,image/png,image/jpeg,image/webp' : 'video/mp4'}
          className="hidden"
          data-testid={`video-upload-${fieldKey}`}
          onChange={handleFile}
        />
        {(presets.length > 0 || myVideos.length > 0) && (
          <button
            type="button"
            data-testid="filler-browse-all"
            disabled={disabled}
            onClick={() => !disabled && setBrowseOpen(true)}
            className="flex items-center justify-center text-xs px-2 py-1.5 rounded-md border border-border bg-muted/30 hover:bg-muted/50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('projectClips.postLayouts.satisfatorio.browseAll', {
              count: presets.length + myVideos.length,
            })}
          </button>
        )}
        <button
          type="button"
          disabled={isLoading || disabled}
          onClick={() => fileInputRef.current?.click()}
          className="flex items-center justify-center gap-1.5 text-xs px-2 py-1.5 rounded-md border border-border bg-muted/30 hover:bg-muted/50 disabled:opacity-50 whitespace-nowrap"
        >
          {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <Upload className="h-3.5 w-3.5" aria-hidden="true" />}
          {isLoading ? t('projectClips.postLayouts.uploading') : t('projectClips.postLayouts.upload')}
        </button>
        {allowStillImages && (
        <button
          type="button"
          data-testid={`video-image-library-${fieldKey}`}
          disabled={disabled}
          onClick={() => setImagePickerOpen(true)}
          className="flex items-center justify-center gap-1.5 text-xs px-2 py-1.5 rounded-md border border-border bg-muted/30 hover:bg-muted/50 disabled:opacity-50 whitespace-nowrap"
        >
          <Images className="h-3.5 w-3.5" aria-hidden="true" />
          {t('myMedia.picker.images')}
        </button>
        )}
        {presets.length > 0 && (
          <button
            type="button"
            data-testid="filler-autofill-random"
            disabled={disabled}
            aria-pressed={!!fillerRandomize}
            onClick={() => !disabled && autoFillRandom()}
            title={t('projectClips.postLayouts.satisfatorio.autoFillRandom')}
            className={cn(
              'flex items-center justify-center gap-1 text-xs px-2 py-1.5 rounded-md border whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed',
              fillerRandomize
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border bg-muted/30 hover:bg-muted/50',
            )}
          >
            <Shuffle className="h-3.5 w-3.5" aria-hidden="true" />
            {t('projectClips.postLayouts.satisfatorio.autoFillRandom')}
          </button>
        )}
        {allowStillImages && videoFrameSource?.videoUrl && (
          <button
            type="button"
            data-testid={`capture-frame-${fieldKey}`}
            disabled={capturing || isLoading || disabled}
            onClick={handleCaptureFrame}
            className="col-span-2 flex items-center justify-center gap-1.5 text-xs px-2 py-1.5 rounded-md border border-border bg-muted/30 hover:bg-muted/50 disabled:opacity-50 text-center leading-snug"
          >
            {capturing ? <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" /> : <Camera className="h-3.5 w-3.5 shrink-0" />}
            {capturing
              ? t('projectClips.postLayouts.fofoca.capturingFrame')
              : captureFrameLabel(videoFrameSource, t)}
          </button>
        )}
      </div>
      {(error || captureError) && <p className="text-[11px] text-destructive">{captureError || error}</p>}
      <FillerVideoBrowseDialog
        previewSources={fillerPreviews}
        open={browseOpen}
        onOpenChange={setBrowseOpen}
        presets={presets}
        myVideos={myVideos}
        tags={tags}
        selectedUrls={playlist}
        presetUrl={presetUrl}
        onConfirm={applyPlaylist}
      />
      <MyMediaPickerDialog
        open={imagePickerOpen}
        onOpenChange={setImagePickerOpen}
        onSelect={(item) => {
          applyPlaylist(playlist.includes(item.url) ? playlist : [...playlist, item.url]);
        }}
        onUploadInstead={() => {
          setImagePickerOpen(false);
          fileInputRef.current?.click();
        }}
      />
    </div>
  );
};

export const LayoutSettingsPanel: React.FC<{
  fields: FieldDescriptor[];
  config: PostLayoutConfig;
  onChange: (key: string, value: PostLayoutFieldValue) => void;
  /** Keys whose inputs should be rendered as disabled (locked by campaign). */
  disabledKeys?: string[];
  /** Clip duration (seconds), used by the filler auto-fill to size its pick. */
  videoTargetDurationSec?: number;
  /** Templates store a randomize flag instead of baking one shuffle. */
  deferRandomFillers?: boolean;
  /** Optional editor playhead source used by image fields that capture a still. */
  videoFrameSource?: VideoFrameSource;
  fieldBinds?: LayoutFieldBinds;
  onFieldBindChange?: (key: string, source: LayoutBindSource | null) => void;
  bindContext?: LayoutBindUiContext;
}> = ({
  fields,
  config,
  onChange,
  disabledKeys,
  videoTargetDurationSec,
  videoFrameSource,
  deferRandomFillers,
  fieldBinds,
  onFieldBindChange,
  bindContext,
}) => {
  const { t } = useTranslation();
  const val = (key: string) => String((config as unknown as Record<string, unknown>)[key] ?? '');
  // Compact sizing on top of the shadcn Input/Textarea base styles.
  const inputCls = 'h-8 px-2 text-xs';
  const isDisabled = (key: string) => (disabledKeys ?? []).includes(key);

  return (
    <div className="space-y-3">
      {config.kind === 'fofoca' && (
        <div className="flex items-start gap-2 rounded-lg border border-border/70 bg-muted/25 px-2.5 py-2 text-[11px] leading-relaxed text-muted-foreground">
          <Images className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
          <p>{t('projectClips.postLayouts.fofoca.assetReuseHint')}</p>
        </div>
      )}
      {fields.map((f, i) => {
        const disabled = isDisabled(f.key);
        const startsSection = !!f.sectionKey && fields[i - 1]?.sectionKey !== f.sectionKey;
        const identitySources = onFieldBindChange
          ? identityBindSourcesForField(config.kind, f.key)
          : [];
        const boundSource = fieldBinds?.[f.key];
        const bindSource = onFieldBindChange && bindContext
          ? bindControlForField(config.kind, f.key, { hasChannel: !!bindContext.hasChannel })
          : null;
        const isIdentityBound = !!boundSource && identitySources.includes(boundSource);
        const isBound = (!!bindSource && boundSource === bindSource) || isIdentityBound;
        const resolved = boundSource && bindContext
          ? resolveBindPreview(boundSource, bindContext)
          : undefined;
        const boundDisplay = isIdentityBound
          ? (resolved || val(f.key))
          : (isBound ? (resolved ?? '') : val(f.key));
        const bindCopy = bindSource ? bindToggleCopy(bindSource) : null;
        const bag = config as unknown as Record<string, unknown>;
        const urlsKey = f.type === 'video' ? (f.urlsKey ?? 'fillerUrls') : 'fillerUrls';
        const fitsKey = f.type === 'video' ? (f.fitsKey ?? 'fillerFits') : 'fillerFits';
        const positionsKey = f.type === 'video' ? (f.positionsKey ?? 'fillerPositions') : 'fillerPositions';
        const randomizeKey = f.type === 'video' ? (f.randomizeKey ?? 'fillerRandomize') : 'fillerRandomize';
        const fillerPlaylist = f.type === 'video'
          ? resolveFillerPlaylist({
              fillerUrl: val(f.key),
              fillerUrls: bag[urlsKey] as string[] | undefined,
              imageUrl: config.kind === 'fofoca' ? undefined : (config as { imageUrl?: string }).imageUrl,
            })
          : [];
        const toggleValue = config.kind === 'fofoca' && f.key === 'storyMedia'
          ? resolveFofocaStoryMedia(config)
          : val(f.key);
        return (
          <div className="space-y-1" key={f.key}>
            {startsSection && (
              <p className={`text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/80 ${i > 0 ? 'pt-2 border-t border-border/60' : ''}`}>
                {t(f.sectionKey!)}
              </p>
            )}
            {!f.hideLabel && (
            <label className={`text-xs ${disabled ? 'text-muted-foreground/60' : 'text-muted-foreground'} flex items-center gap-1`}>
              {disabled && (
                <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect width="18" height="11" x="3" y="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              )}
              {t(f.labelKey)}
              {bindSource && bindCopy && bindSource !== 'clipTitle' && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      data-testid={`layout-bind-${f.key}`}
                      aria-pressed={isBound}
                      title={t(bindCopy.hintKey)}
                      onClick={() => onFieldBindChange?.(f.key, isBound ? null : bindSource)}
                      className={`ml-auto h-6 shrink-0 whitespace-nowrap rounded-md border px-1.5 text-[10px] leading-none ${
                        isBound
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border bg-muted/30 text-muted-foreground hover:bg-muted/50'
                      }`}
                    >
                      {t(bindCopy.labelKey)}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    data-testid={`layout-bind-${f.key}-tooltip`}
                    className="max-w-[220px] text-center"
                  >
                    {t(bindCopy.hintKey)}
                  </TooltipContent>
                </Tooltip>
              )}
            </label>
            )}
            {f.type === 'text' && !f.multiline && (
              <div className="relative">
                <Input
                  data-testid={`layout-field-${f.key}`}
                  type="text"
                  value={boundDisplay}
                  disabled={disabled}
                  onChange={(e) => {
                    if (isBound) onFieldBindChange?.(f.key, null);
                    onChange(f.key, e.target.value);
                  }}
                  className={cn(
                    inputCls,
                    bindSource === 'clipTitle' && CLIP_TITLE_INPUT_PAD,
                    identitySources.length > 0 && IDENTITY_INPUT_PAD,
                  )}
                />
                {bindSource === 'clipTitle' && (
                  <ClipTitleBindToggle
                    fieldKey={f.key}
                    isBound={isBound}
                    disabled={disabled}
                    label={t('projectClips.postLayouts.binds.useClipTitle')}
                    hint={t('projectClips.postLayouts.binds.clipTitleHint')}
                    onToggle={() => onFieldBindChange?.(f.key, isBound ? null : bindSource)}
                  />
                )}
                {identitySources.length > 0 && (
                  <IdentityBindButtons
                    fieldKey={f.key}
                    sources={identitySources}
                    boundSource={boundSource}
                    disabled={disabled}
                    onToggle={(source) => onFieldBindChange?.(f.key, source)}
                  />
                )}
              </div>
            )}
            {f.type === 'text' && f.multiline && (
              <div className="relative">
                <Textarea
                  data-testid={`layout-field-${f.key}`}
                  rows={3}
                  value={isBound ? (resolved ?? '') : val(f.key)}
                  disabled={disabled}
                  onChange={(e) => {
                    if (isBound) onFieldBindChange?.(f.key, null);
                    onChange(f.key, e.target.value);
                  }}
                  className={cn('min-h-0 resize-none px-2 py-1.5 text-xs', bindSource === 'clipTitle' && CLIP_TITLE_INPUT_PAD)}
                />
                {bindSource === 'clipTitle' && (
                  <ClipTitleBindToggle
                    fieldKey={f.key}
                    isBound={isBound}
                    disabled={disabled}
                    align="start"
                    label={t('projectClips.postLayouts.binds.useClipTitle')}
                    hint={t('projectClips.postLayouts.binds.clipTitleHint')}
                    onToggle={() => onFieldBindChange?.(f.key, isBound ? null : bindSource)}
                  />
                )}
              </div>
            )}
            {f.type === 'font' && (
              <FontSelector
                value={val(f.key)}
                placeholder={val(f.key)}
                label=""
                showPreview={false}
                disabled={disabled}
                className="space-y-0"
                onValueChange={(fontId, font) => {
                  onChange(f.key, fontId);
                  onChange(f.urlKey, font.file_url);
                }}
              />
            )}
            {f.type === 'image' && (
              <ImageField
                fieldKey={f.key}
                value={isIdentityBound ? boundDisplay : (isBound ? (resolved ?? val(f.key)) : val(f.key))}
                inputCls={inputCls}
                disabled={disabled || isBound}
                allowVideoFrame={f.allowVideoFrame}
                videoFrameSource={videoFrameSource}
                onChange={onChange}
                endAdornment={identitySources.length > 0 ? (
                  <IdentityBindButtons
                    fieldKey={f.key}
                    sources={identitySources}
                    boundSource={boundSource}
                    disabled={disabled}
                    onToggle={(source) => onFieldBindChange?.(f.key, source)}
                  />
                ) : undefined}
              />
            )}
            {f.type === 'color' && (
              <div className="flex items-center gap-2">
                <ColorInput value={val(f.key) || '#000000'} disabled={disabled} onChange={(v) => onChange(f.key, v)} testId={`color-${f.key}`} />
                {f.key === 'backgroundColor' && (
                  <button
                    type="button"
                    data-testid={`color-${f.key}-clear`}
                    disabled={disabled}
                    onClick={() => onChange(f.key, '')}
                    className="h-8 rounded-md border border-border px-2 text-xs text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {t('common.clear')}
                  </button>
                )}
              </div>
            )}
            {f.type === 'video' && (
              <VideoField
                fieldKey={f.key}
                value={val(f.key) || fillerPlaylist[0] || ''}
                playlist={fillerPlaylist}
                targetDurationSec={videoTargetDurationSec}
                fillerSpeed={(config as { fillerSpeed?: number }).fillerSpeed}
                fits={bag[fitsKey] as Record<string, FillerFit> | undefined}
                positions={bag[positionsKey] as Record<string, FillerPosition> | undefined}
                urlsKey={urlsKey}
                fitsKey={fitsKey}
                positionsKey={positionsKey}
                randomizeKey={randomizeKey}
                disabled={disabled}
                videoFrameSource={videoFrameSource}
                deferRandomFillers={deferRandomFillers}
                fillerRandomize={bag[randomizeKey] as boolean | undefined}
                allowStillImages={config.kind !== 'fofoca'}
                onChange={onChange}
              />
            )}
            {f.type === 'slider' && (
              <div className="flex items-center gap-2">
                <Slider
                  min={f.min}
                  max={f.max}
                  step={f.step}
                  value={[Number(val(f.key)) || f.min]}
                  data-testid={`slider-${f.key}`}
                  disabled={disabled}
                  onValueChange={([v]) => onChange(f.key, v)}
                  className="flex-1"
                />
                <span className="text-xs w-10 text-right tabular-nums">{Number(val(f.key)) || f.min}{f.unit ?? '%'}</span>
              </div>
            )}
            {f.type === 'toggle' && (
              <div
                className={f.hideLabel ? 'grid grid-cols-2 gap-0.5 rounded-md bg-muted p-0.5' : 'flex gap-1'}
                role={f.hideLabel ? 'tablist' : undefined}
              >
                {f.options.map((opt) => {
                  const isSelected = toggleValue === opt.value;
                  return (
                  <button
                    key={opt.value}
                    type="button"
                    role={f.hideLabel ? 'tab' : undefined}
                    data-testid={`toggle-${f.key}-${opt.value}`}
                    aria-pressed={isSelected}
                    aria-selected={f.hideLabel ? isSelected : undefined}
                    disabled={disabled}
                    onClick={() => !disabled && onChange(f.key, opt.value)}
                    className={f.hideLabel
                      ? `h-7 text-xs rounded-sm disabled:opacity-50 disabled:cursor-not-allowed ${
                          isSelected ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                        }`
                      : `flex-1 text-xs px-2 py-1.5 rounded-md border disabled:opacity-50 disabled:cursor-not-allowed ${
                          isSelected
                            ? 'border-primary bg-primary/10'
                            : 'border-border bg-muted/30 hover:bg-muted/50'
                        }`}
                  >
                    {t(opt.labelKey)}
                  </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
